/**
 * Utilidades para parsear nombres hispanos y validar identificadores de paciente.
 */

export interface ParsedPatientName {
  firstName: string;
  lastName: string;
}

const INVALID_ID_TOKENS = new Set([
  'is', 'el', 'la', 'los', 'las', 'de', 'del', 'en', 'un', 'una', 'id', 'no', 'si'
]);

/**
 * Divide un nombre completo en nombre(s) y apellido(s).
 * Soporta formato "Apellidos, Nombres" y orden natural "Nombres Apellidos".
 */
export function parseFullName(fullName: string): ParsedPatientName {
  const trimmed = fullName.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return { firstName: '', lastName: '' };
  }

  if (trimmed.includes(',')) {
    const [lastPart, firstPart] = trimmed.split(',').map(part => part.trim());
    if (firstPart && lastPart) {
      return { firstName: firstPart, lastName: lastPart };
    }
  }

  const parts = trimmed.split(' ');
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  if (parts.length === 2) {
    return { firstName: parts[0], lastName: parts[1] };
  }
  if (parts.length === 3) {
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }

  // 4+ palabras sin coma: asumir 2 apellidos al final (convención IMSS)
  const surnameCount = 2;
  const givenNameCount = parts.length - surnameCount;
  if (givenNameCount >= 1) {
    return {
      firstName: parts.slice(0, givenNameCount).join(' '),
      lastName: parts.slice(givenNameCount).join(' ')
    };
  }

  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/**
 * Rechaza IDs demasiado cortos o fragmentos de texto (p. ej. "is" de narrativa en español).
 */
export function isValidPatientId(id: string): boolean {
  const trimmed = id.trim();
  if (trimmed.length < 4) return false;
  if (INVALID_ID_TOKENS.has(trimmed.toLowerCase())) return false;
  if (!/^[A-Za-z0-9\-\/\.]+$/.test(trimmed)) return false;
  if (/\d/.test(trimmed)) return true;
  return trimmed.length >= 6;
}

/**
 * Calcula edad a partir de una fecha en formatos comunes dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd.
 */
export function ageFromBirthDate(dateStr: string): number | undefined {
  const trimmed = dateStr.trim();
  let year: number | undefined;
  let month: number | undefined;
  let day: number | undefined;

  const isoMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (isoMatch) {
    year = parseInt(isoMatch[1], 10);
    month = parseInt(isoMatch[2], 10);
    day = parseInt(isoMatch[3], 10);
  } else {
    const dmyMatch = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
    if (!dmyMatch) return undefined;
    day = parseInt(dmyMatch[1], 10);
    month = parseInt(dmyMatch[2], 10);
    year = parseInt(dmyMatch[3], 10);
    if (year < 100) year += year > 30 ? 1900 : 2000;
  }

  if (!year || !month || !day) return undefined;

  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return undefined;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age >= 0 && age <= 120 ? age : undefined;
}

export function birthDateFromAge(age: number): string {
  const year = new Date().getFullYear() - age;
  return `${year}-01-01`;
}
